// El título de un chat lo escribe la IA (ADR-2026-10-13-el-titulo-del-chat-lo-escribe-la-ia).
//
// Los chats del Agente y las sesiones del Asistente de Crear se reconocen en el Historial por
// su título, que nacía como el primer mensaje cortado o "Sesión 13 oct". Apenas la persona
// manda su PRIMER mensaje, se le manda al backend lo que hay de la conversación y Gemini Flash
// devuelve un título corto que dice de qué se trata; se guarda en `agent_chats.title` y se
// avisa a quien lo muestra (ADR-2026-10-13-el-titulo-del-chat-lo-escribe-la-ia-2). Una vez por
// chat; si la charla todavía no tiene tema (sólo un saludo), se vuelve a probar con el mensaje
// siguiente de la persona, hasta el tercero. Nunca tira: si algo falla, el chat se queda con el
// título que tenía.

import { atlasBackendFetch } from '../../lib/atlasBackend';
import { leerTipoDeChat, renombrarChat, TIPO_CHAT_REDES } from './agentChatApi';
import type { V2ChatMessage } from './useV2Chat';

/** Hasta qué mensaje de la persona se sigue probando si la charla no tenía tema. */
export const TURNOS_PARA_TITULAR = 3;
/** Lo que se manda de cada lado: para saber de qué se trata alcanza con el arranque. */
const MAX_CHARS_POR_MENSAJE = 1_500;
const TIMEOUT_MS = 20_000;

/** Se dispara en `window` cuando un chat recibe su título: `{ chatId, titulo }`. */
export const EVENTO_CHAT_TITULADO = 'atlas:chat-titulado';
export interface DetalleChatTitulado {
  chatId: string;
  titulo: string;
}

const recortar = (t: string) => {
  const limpio = t.replace(/\s+/g, ' ').trim();
  return limpio.length > MAX_CHARS_POR_MENSAJE ? `${limpio.slice(0, MAX_CHARS_POR_MENSAJE)}…` : limpio;
};

/**
 * Con cuántos mensajes de la persona se puede pedir el título ahora: entre uno y
 * `TURNOS_PARA_TITULAR`, con algo escrito. 0 si no toca. Quién ya tiene título (y con cuántos
 * mensajes se probó) lo lleva el que llama.
 */
export function mensajesParaTitular(mensajes: V2ChatMessage[]): number {
  const deLaPersona = mensajes.filter((m) => m.role === 'user');
  if (deLaPersona.length < 1 || deLaPersona.length > TURNOS_PARA_TITULAR) return 0;
  return deLaPersona.some((m) => m.text.trim()) ? deLaPersona.length : 0;
}

/** El arranque de la conversación, en limpio: el pedido y lo que respondió el asistente. */
export function transcripcionParaTitulo(mensajes: V2ChatMessage[]): string {
  const renglones: string[] = [];
  for (const m of mensajes) {
    if (m.kind === 'upgrade') continue;
    const texto = recortar(m.text ?? '');
    if (!texto) continue;
    renglones.push(`${m.role === 'user' ? 'La persona' : 'Asistente'}: ${texto}`);
  }
  return renglones.join('\n');
}

/** Cómo terminó el intento: con título, sin tema todavía (se reintenta) o con un error. */
export type ResultadoDeTitulo = { tipo: 'titulado'; titulo: string } | { tipo: 'sin-tema' } | { tipo: 'fallo' };

/** Le pide el título al backend. Nunca tira. */
export async function pedirTituloDeChat(transcripcion: string, chatId: string | null): Promise<ResultadoDeTitulo> {
  try {
    const res = await atlasBackendFetch(
      '/v1/agent/titulo',
      { transcripcion, ...(chatId ? { agent_chat_id: chatId } : {}) },
      { signal: AbortSignal.timeout(TIMEOUT_MS) },
    );
    if (!res.ok) return { tipo: 'fallo' };
    const json = (await res.json()) as { titulo?: unknown };
    if (json.titulo === null) return { tipo: 'sin-tema' };
    const titulo = typeof json.titulo === 'string' ? json.titulo.trim() : '';
    return titulo ? { tipo: 'titulado', titulo } : { tipo: 'fallo' };
  } catch {
    return { tipo: 'fallo' };
  }
}

/**
 * Titula el chat y lo guarda. Sólo los del Agente (`tipo` null) y las sesiones de Crear: un
 * diseño (u otro tipo) tiene el nombre que le puso la persona (cuenta como hecho: no hay que
 * volver a probar).
 */
export async function titularChat(chatId: string, mensajes: V2ChatMessage[]): Promise<ResultadoDeTitulo> {
  const transcripcion = transcripcionParaTitulo(mensajes);
  if (!transcripcion) return { tipo: 'sin-tema' };
  const tipo = await leerTipoDeChat(chatId);
  if (tipo === undefined) return { tipo: 'fallo' };
  if (tipo !== null && tipo !== TIPO_CHAT_REDES) return { tipo: 'fallo' };
  const r = await pedirTituloDeChat(transcripcion, chatId);
  if (r.tipo !== 'titulado') return r;
  if (!(await renombrarChat(chatId, r.titulo))) return { tipo: 'fallo' };
  window.dispatchEvent(
    new CustomEvent<DetalleChatTitulado>(EVENTO_CHAT_TITULADO, { detail: { chatId, titulo: r.titulo } }),
  );
  return r;
}
